import { useEffect, useRef, useState } from 'react';

const serviceTabs = [
  {
    id: 'field',
    label: 'Field Services',
    icon: 'ri-map-pin-2-line',
    intro:
      'Our field services team provides boots-on-the-ground support for every phase of telecommunications projects, from initial site assessments to final inspections.',
    items: [
      'Site Design Walks',
      '1A/2C Certifications',
      'Site & Right-of-Way Surveys',
      'Utility Coordination',
      'Geotechnical Investigations',
      'Grading & Drainage Evaluations',
      'Fiber Inspections',
      'Drone Surveys & Inspections',
      'Grounding System Testing',
    ],
  },
  {
    id: 'engineering',
    label: 'Engineering & Design',
    icon: 'ri-draft-line',
    intro:
      'Our engineering and design team delivers precise, code-compliant drawings and technical documents supporting every stage of wireless and wireline infrastructure development.',
    items: [
      'Lease Exhibit & Zoning Drawings',
      'Construction Drawings',
      'Tenant Improvement Space Design',
      'Concealment & Stealth Concepts',
      'Site Power Design',
      'Lightning Protection & Grounding System Design',
      'Traffic Control Plans',
      'Photo Sims & Visual Impact Studies',
      'ISP & OSP Design',
    ],
  },
  {
    id: 'structural',
    label: 'Structural Engineering',
    icon: 'ri-building-2-line',
    intro:
      'Our structural engineering practice specializes in tower systems, providing comprehensive analysis, modification design, and integrity assessments for critical telecommunications infrastructure.',
    items: [
      'Tower Analysis & Modification',
      'Mount Analysis & Reinforcement',
      'Tower Foundation Modification & Reinforcement Design',
      'Tower Load & Mount Mapping',
      'Structural Integrity Inspections',
      'Corrosion Assessment & Remediation Planning',
      'Structural Feasibility Study',
    ],
  },
  {
    id: 'siteacq',
    label: 'Site Acquisition',
    icon: 'ri-search-eye-line',
    intro:
      'Our site acquisition team manages the full lifecycle of securing telecom sites, from identifying candidates and negotiating leases to navigating zoning, permitting, and property owner relations.',
    items: [
      'Site Identification & Candidate Evaluation',
      'Real Estate & Lease Negotiation',
      'Zoning & Land Use Entitlements',
      'Local/State/Federal Permitting',
      'Small Cell & ROW Siting',
      'Master License Agreement Negotiations',
      'Pole & Streetlight Attachment Coordination',
      'Title Research & Due Diligence Coordination',
      'Lease Audit & Compliance Review',
      'Easement Acquisition',
      'Property Owner Relations',
    ],
  },
  {
    id: 'program',
    label: 'Program & Construction Mgmt',
    icon: 'ri-settings-3-line',
    intro:
      'Our program and construction management team ensures projects are delivered on time, on budget, and to spec, overseeing everything from scope development to final closeout.',
    items: [
      'Program Oversight & Scheduling',
      'Scope Development & Validation',
      'Contractor & Vendor Management',
      'Construction Inspection & Quality Assurance',
      'Materials Coordination & Tracking',
      'Regulatory Compliance Monitoring',
      'Site Access Coordination',
      'Milestone Reporting & Project Closeout',
    ],
  },
];

export default function ServicesSection() {
  const [activeTab, setActiveTab] = useState('field');
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const children = sectionRef.current?.querySelectorAll('.fade-in-child');
            children?.forEach((child, i) => {
              const el = child as HTMLElement;
              setTimeout(() => {
                el.style.transition = 'opacity 0.7s ease, transform 0.7s ease';
                el.style.opacity = '1';
                el.style.transform = 'translateY(0)';
              }, i * 80);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const active = serviceTabs.find((t) => t.id === activeTab)!;

  return (
    <section ref={sectionRef} className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p
            className="fade-in-child text-[#0050B2] text-sm font-semibold uppercase tracking-widest mb-3"
            style={{ opacity: 0, transform: 'translateY(20px)' }}
          >
            Our Services
          </p>
          <h2
            className="fade-in-child text-3xl lg:text-4xl font-bold text-[#001F4E]"
            style={{ opacity: 0, transform: 'translateY(20px)' }}
          >
            Comprehensive Telecom
            <br />
            <span className="text-[#0050B2]">Capabilities</span>
          </h2>
        </div>

        {/* Tabs */}
        <div
          className="fade-in-child flex flex-wrap justify-center gap-2 mb-10"
          style={{ opacity: 0, transform: 'translateY(20px)' }}
        >
          {serviceTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap border ${
                activeTab === tab.id
                  ? 'bg-[#0050B2] text-white border-[#0050B2]'
                  : 'bg-white text-[#001F4E] border-[#001F4E]/15 hover:border-[#0050B2]/40 hover:bg-[#E8EFF6]'
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={tab.icon} />
              </div>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content Panel */}
        <div
          key={activeTab}
          className="bg-[#F4F7FB] rounded-lg p-8 lg:p-10"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-md bg-[#0050B2]">
                  <i className={`${active.icon} text-white text-lg`} />
                </div>
                <h3 className="text-xl font-bold text-[#001F4E]">{active.label}</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed">{active.intro}</p>


            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {active.items.map((item, i) => (
                <div key={i} className="flex items-start gap-2.5 bg-white rounded-md px-4 py-3">
                  <div className="w-5 h-5 flex items-center justify-center mt-0.5 shrink-0">
                    <i className="ri-checkbox-circle-fill text-[#0050B2] text-base" />
                  </div>
                  <span className="text-[#001F4E] text-sm font-medium leading-snug">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
